import { gql } from "graphql-request";
import type { GraphQLClient } from "graphql-request";
import type { InfringementRecordsData, InfringementRecordEntry } from "@nexus/widgets";
import type { DataResolver } from "@/lib/dashboard/resolverTypes";
import { getFilterValue, getFilterValues, getTenantIds } from "./shared";

// ── Infringement Records ──────────────────────────────────────────────────────

const INFRINGEMENT_RECORDS_QUERY = gql`
  query InfringementRecords(
    $protected_event_id: Int!
    $from_ts: String!
    $to_ts: String!
    $tenant_ids: [Int!]
    $infringement_types: [String!]
    $response_statuses: [String!]
    $search: String
    $first: Int
    $after: String
  ) {
    infringement_records(
      protected_event_id: $protected_event_id
      from_ts: $from_ts
      to_ts: $to_ts
      tenant_ids: $tenant_ids
      infringement_types: $infringement_types
      response_statuses: $response_statuses
      search: $search
      first: $first
      after: $after
    ) {
      protected_event_id
      event_id
      event_label
      event_type
      event_start
      event_end
      stream_id
      stream_label
      channel
      rightholder
      tenant_id
      tenant_label
      customer
      has_more
      page_info {
        has_next_page
        end_cursor
      }
      record_count
      response_count
      records {
        infringement_id
        infringement_name
        infringement_type
        infringement_entity
        engine_id
        engine_label
        engine_infringement_id
        tenant_id
        tenant_label
        customer
        detection_domain
        first_detection
        last_detection
        latest_response_status
        pirate_endpoint_ids
        confidence_score
        severity
        identifiers {
          type
          value
        }
        domains
        stream_ids
        protected_event_ids
        detection_count
        is_actionable
        not_actionable_reason
        origin_engine_id
        origin_engine_label
        response_count
        confirmed_response_count
        pending_response_count
        failed_response_count
        cancelled_response_count
        latest_response_at
        responses {
          response_id
          action_id
          action_type
          engine_id
          engine_label
          scenario_response_id
          identifiers {
            type
            value
          }
          response_type
          response_status
          execution_mode
          contact_id
          contact_name
          contact_country
          contact_mail
          trigger_date
          response_start
          response_end
          protected_event_id
        }
      }
    }
  }
`;

type RawIdentifier = {
  type: string;
  value: string;
};

type RawResponse = {
  response_id: string;
  action_id: string;
  action_type: string;
  engine_id: number | null;
  engine_label: string | null;
  scenario_response_id: number | null;
  identifiers: RawIdentifier[] | null;
  response_type: string;
  response_status: string;
  execution_mode: string;
  contact_id: number | null;
  contact_name: string | null;
  contact_country: string | null;
  contact_mail: string | null;
  trigger_date: string;
  response_start: string | null;
  response_end: string | null;
  protected_event_id: number | null;
};

type RawRecord = {
  infringement_id: string;
  infringement_name: string;
  infringement_type: string;
  infringement_entity: string | null;
  engine_id: number;
  engine_label: string;
  engine_infringement_id: string;
  tenant_id: number;
  tenant_label: string;
  customer: string | null;
  detection_domain: string;
  first_detection: string;
  last_detection: string;
  latest_response_status: string | null;
  pirate_endpoint_ids: number[];
  confidence_score: number;
  severity: number | null;
  identifiers: RawIdentifier[] | null;
  domains: string[];
  stream_ids: number[];
  protected_event_ids: number[];
  detection_count: number;
  is_actionable: boolean;
  not_actionable_reason: string | null;
  origin_engine_id: number | null;
  origin_engine_label: string | null;
  response_count: number;
  confirmed_response_count: number;
  pending_response_count: number;
  failed_response_count: number;
  cancelled_response_count: number;
  latest_response_at: string | null;
  responses: RawResponse[];
};

type InfringementRecordsResponse = {
  infringement_records: {
    protected_event_id: number;
    event_id: string | null;
    event_label: string;
    event_type: string;
    event_start: string | null;
    event_end: string | null;
    stream_id: string | null;
    stream_label: string;
    channel: string;
    rightholder: string;
    tenant_id: number;
    tenant_label: string;
    customer: string | null;
    has_more: boolean;
    page_info?: { has_next_page: boolean; end_cursor: string | null };
    record_count: number;
    response_count: number;
    records: RawRecord[];
  };
};

/** Adapt backend identifiers to the grouped targets used by response widgets. */
function normalizeResponseTargets(identifiers: RawIdentifier[] | null): Record<string, string[]> {
  const targets = new Map<string, string[]>();
  for (const { type, value } of identifiers ?? []) {
    const key = type === "ip" ? "ips" : type === "account" ? "account_ids" : type;
    const values = targets.get(key) ?? [];
    values.push(value);
    targets.set(key, values);
  }
  return Object.fromEntries(targets);
}

function normalizeInfringementRecords(raw: unknown): InfringementRecordsData {
  const data = raw as InfringementRecordsResponse;
  const result = data.infringement_records;
  return {
    protected_event_id: Number(result.protected_event_id),
    event_id: result.event_id,
    event_label: result.event_label,
    event_type: result.event_type,
    event_start: result.event_start,
    event_end: result.event_end,
    stream_id: result.stream_id,
    stream_label: result.stream_label,
    channel: result.channel,
    rightholder: result.rightholder,
    tenant_id: Number(result.tenant_id),
    tenant_label: result.tenant_label,
    customer: result.customer,
    has_more: Boolean(result.has_more),
    page_info: result.page_info,
    record_count: Number(result.record_count ?? 0),
    response_count: Number(result.response_count ?? 0),
    records: (result.records ?? []).map((r): InfringementRecordEntry => ({
      infringement_id: String(r.infringement_id),
      infringement_name: r.infringement_name,
      infringement_type: r.infringement_type,
      infringement_entity: r.infringement_entity,
      engine_id: Number(r.engine_id),
      engine_label: r.engine_label,
      engine_infringement_id: r.engine_infringement_id,
      tenant_id: Number(r.tenant_id),
      tenant_label: r.tenant_label,
      customer: r.customer,
      detection_domain: r.detection_domain,
      first_detection: r.first_detection,
      last_detection: r.last_detection,
      latest_response_status: r.latest_response_status,
      pirate_endpoint_ids: r.pirate_endpoint_ids ?? [],
      confidence_score: Number(r.confidence_score),
      severity: r.severity != null ? Number(r.severity) : null,
      ips: (r.identifiers ?? []).filter(({ type }) => type === "ip").map(({ value }) => value),
      accounts: (r.identifiers ?? [])
        .filter(({ type }) => type === "account")
        .map(({ value }) => value),
      tokens: (r.identifiers ?? [])
        .filter(({ type }) => type === "token_id")
        .map(({ value }) => value),
      domains: r.domains ?? [],
      stream_ids: r.stream_ids ?? [],
      protected_event_ids: r.protected_event_ids ?? [],
      detection_count: Number(r.detection_count),
      is_actionable: Boolean(r.is_actionable),
      not_actionable_reason: r.not_actionable_reason,
      extra: null,
      response_count: Number(r.response_count),
      confirmed_response_count: Number(r.confirmed_response_count),
      pending_response_count: Number(r.pending_response_count),
      failed_response_count: Number(r.failed_response_count),
      cancelled_response_count: Number(r.cancelled_response_count),
      latest_response_at: r.latest_response_at,
      origin_engine_id: r.origin_engine_id != null ? Number(r.origin_engine_id) : null,
      origin_engine_label: r.origin_engine_label ?? null,
      responses: (r.responses ?? []).map((response) => ({
        response_id: String(response.response_id),
        action_id: String(response.action_id),
        action_type: response.action_type,
        engine_id: response.engine_id != null ? Number(response.engine_id) : null,
        engine_label: response.engine_label,
        scenario_response_id:
          response.scenario_response_id != null ? Number(response.scenario_response_id) : null,
        targets: normalizeResponseTargets(response.identifiers),
        response_type: response.response_type,
        response_status: response.response_status,
        execution_mode: response.execution_mode,
        contact_id: response.contact_id != null ? Number(response.contact_id) : null,
        contact_name: response.contact_name,
        contact_country: response.contact_country,
        contact_mail: response.contact_mail,
        trigger_date: response.trigger_date,
        response_start: response.response_start,
        response_end: response.response_end,
        protected_event_id:
          response.protected_event_id != null ? Number(response.protected_event_id) : null,
      })),
    })),
  };
}

function mergeInfringementPages(pages: unknown[]): InfringementRecordsData {
  const typed = pages as InfringementRecordsData[];
  const first = typed[0];
  if (!first)
    return {
      protected_event_id: 0,
      event_id: null,
      event_label: "",
      event_type: "",
      event_start: null,
      event_end: null,
      stream_id: null,
      stream_label: "",
      channel: "",
      rightholder: "",
      tenant_id: 0,
      tenant_label: "",
      customer: null,
      has_more: false,
      record_count: 0,
      response_count: 0,
      records: [],
    };
  return {
    ...first,
    has_more: false,
    page_info: typed[typed.length - 1]?.page_info,
    records: typed.flatMap((page) => page.records),
  };
}

// ── Resolvers ─────────────────────────────────────────────────────────────────

export const infringementDataSources: Record<string, DataResolver> = {
  "infringement-records": {
    contract: { name: "infringement-records", version: 1 },
    buildParams: (requestParams, filters) => ({
      protected_event_id: (() => {
        const protectedEventId = getFilterValue(filters, "protected_event_id");
        return protectedEventId ? Number(protectedEventId) : 0;
      })(),
      from_ts: filters.timeRange.from,
      to_ts: filters.timeRange.to,
      tenant_ids: getTenantIds(filters),
      infringement_types: getFilterValues(filters, "infringement_type"),
      response_statuses: getFilterValues(filters, "latest_response_status"),
      search: getFilterValue(filters, "search"),
      first: typeof requestParams.limit === "number" ? requestParams.limit : 100,
    }),
    fetcher: async (params, client: GraphQLClient) => {
      const raw = await client.request<InfringementRecordsResponse>(
        INFRINGEMENT_RECORDS_QUERY,
        params,
      );
      return normalizeInfringementRecords(raw);
    },
    pagination: {
      getNextCursor: (data) => (data as InfringementRecordsData).page_info?.end_cursor ?? null,
      mergePages: mergeInfringementPages,
    },
  },
};
